import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, FileCheck2, Scale, Compass, Cog, ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { WhatsAppCTA, VoltageButton } from "../components/Buttons";
import { PhoneIconBtn, MailIconBtn } from "../components/ContactIcons";
import ServicesStickyNav from "../components/ServicesStickyNav";
import { SITE } from "../lib/site";
import useSEO from "../hooks/useSEO";

const SERVICES_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Tax & GST Compliance, Audit, Governance and Advisory",
  "provider": { "@id": "https://www.svmd.in/#org" },
  "areaServed": { "@type": "Country", "name": "India" },
  "name": "Tax, GST and Regulatory Compliance Services",
  "description":
    "End-to-end GST, Income Tax, TDS and regulatory compliance, statutory and tax audits, internal control frameworks (ICFR/SOX), governance and Virtual CFO advisory, RERA compliance, payroll and bookkeeping.",
  "url": "https://www.svmd.in/services",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Service catalogue",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "GST Compliance & Advisory" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Income Tax Compliance" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "TDS Compliance" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Tax Planning & Structuring" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Statutory Audits" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Internal Control Frameworks (ICFR / SOX)" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Virtual CFO Services" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "RERA Compliance" } }
    ]
  }
};

const PILLARS = [
  {
    id: "audit",
    n: "01",
    title: "Audit & Assurance",
    intro: "We undertake audit assignments with a focus on reliability, documentation, and regulatory alignment.",
    items: [
      "Statutory Audits",
      "Internal & Management Audits",
      "Tax Audits",
      "Forensic Audits & Investigations",
      "Banking Audits (Stock, Concurrent, Receivables, Credit)",
    ],
    icon: ShieldCheck,
    theme: "dark",
  },
  {
    id: "tax",
    n: "02",
    title: "Tax & Compliance",
    intro: "We manage end-to-end tax and regulatory compliance, ensuring filings are accurate, timely, and consistent.",
    items: [
      "GST Compliance & Advisory",
      "Income Tax Compliance",
      "TDS Compliance",
      "Tax Planning & Structuring",
      "Ongoing Regulatory Compliance",
    ],
    icon: FileCheck2,
    theme: "light",
  },
  {
    id: "representation",
    n: "03",
    title: "Representation & Dispute Resolution",
    intro: "We handle notices, assessments, and disputes across tax and regulatory matters. From initial response to final resolution, we stay involved throughout.",
    items: [
      "Income Tax Notices & Assessments",
      "GST Notices & Proceedings",
      "MSME Dispute Resolution",
      "RERA Representation & Disputes",
      "Appellate Support",
    ],
    icon: Scale,
    theme: "dark",
    subPages: [
      { to: "/representation-and-dispute", label: "View Representation & Dispute Services" },
      { to: "/msme-recovery", label: "MSME Payment Recovery" },
    ],
  },
  {
    id: "governance",
    n: "04",
    title: "Governance & Advisory",
    intro: "We support businesses in building structure around financial processes and controls.",
    items: [
      "Internal Control Frameworks (ICFR)",
      "SOX Compliance & Testing",
      "Risk Assessment & Control Mapping",
      "Virtual CFO Services",
      "Financial Planning & Structuring",
    ],
    icon: Compass,
    theme: "light",
  },
  {
    id: "operations",
    n: "05",
    title: "Finance Operations & Specialised Services",
    intro: "We support ongoing finance operations and specialised compliance requirements.",
    items: [
      "Bookkeeping & Accounting",
      "MIS & Financial Reporting",
      "Payroll & Statutory Support",
      "RERA Compliance (End-to-End)",
      "International Tax & Structuring",
    ],
    icon: Cog,
    theme: "dark",
  },
];

const ENGAGEMENTS = [
  { title: "Specific Engagements", body: "Defined assignments such as audits or notices." },
  { title: "Ongoing Support", body: "Continuous compliance and finance operations." },
  { title: "Integrated Engagements", body: "End-to-end support across multiple areas." },
];

function PillarSection({ p, idx }) {
  const dark = p.theme === "dark";
  const Icon = p.icon;
  return (
    <section
      id={p.id}
      data-testid={`service-pillar-${p.id}`}
      className={`relative overflow-hidden ${dark ? "bg-ink text-paper" : "bg-paper text-ink"}`}
    >
      {dark && <div className="absolute inset-0 grid-bg opacity-60" aria-hidden />}
      {dark && idx % 2 === 0 && (
        <div className="absolute -right-24 top-0 w-[600px] h-[600px] rounded-full blur-3xl opacity-30 bg-[radial-gradient(circle,rgba(37,99,235,0.4)_0%,transparent_60%)]" aria-hidden />
      )}
      <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 md:col-span-5">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <Icon size={22} strokeWidth={1.4} className={dark ? "text-voltage" : "text-electric"} />
                <span className={`font-mono text-[10px] tracking-[0.18em] ${dark ? "text-paper/55" : "text-ink-tertiary"}`}>
                  {p.n} / 05
                </span>
              </div>
              <h2 className="font-display font-semibold text-[30px] md:text-[42px] leading-[1.08] tracking-[-0.025em]">
                {p.title}
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className={`mt-5 text-[15px] md:text-[16px] leading-relaxed max-w-md ${dark ? "text-paper/70" : "text-slate-muted"}`}>
                {p.intro}
              </p>
            </Reveal>

            {p.subPages && (
              <Reveal delay={220}>
                <div className="mt-7 flex flex-col gap-3">
                  <VoltageButton
                    as={Link}
                    to={p.subPages[0].to}
                    withArrow
                    testId={`pillar-${p.id}-sub-0`}
                  >
                    {p.subPages[0].label}
                  </VoltageButton>
                  <Link
                    to={p.subPages[1].to}
                    data-testid={`pillar-${p.id}-sub-1`}
                    className={`inline-flex items-center gap-2 text-[14px] font-medium px-1 py-2 self-start ${
                      dark ? "text-voltage hover:text-voltage-soft" : "text-electric hover:text-ink-700"
                    } link-wipe`}
                  >
                    {p.subPages[1].label}
                    <ArrowRight size={16} strokeWidth={2} />
                  </Link>
                </div>
              </Reveal>
            )}
          </div>

          <div className="col-span-12 md:col-span-7 md:pl-12">
            <Reveal delay={150}>
              <div className={`text-[10px] tracking-[0.18em] uppercase mb-4 ${dark ? "text-paper/55" : "text-ink-tertiary"}`}>
                Services include
              </div>
              <ul className="space-y-1">
                {p.items.map((it, i) => (
                  <li
                    key={it}
                    className={`flex items-center gap-5 py-4 border-b ${
                      dark ? "border-white/10" : "border-[var(--border-light)]"
                    }`}
                  >
                    <span className={`font-mono text-[10px] w-7 ${dark ? "text-paper/45" : "text-ink-tertiary"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <ArrowRight
                      size={16}
                      strokeWidth={1.6}
                      className={`${dark ? "text-voltage" : "text-electric"} opacity-70`}
                    />
                    <span className={`text-[14px] md:text-[15px] flex-1 ${dark ? "text-paper/90" : "text-ink/85"}`}>
                      {it}
                    </span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Services() {
  useSEO({
    title:
      "GST, Income Tax & Audit Compliance Services | SVMD & Associates",
    description:
      "GST, Income Tax, TDS and regulatory compliance, statutory and tax audits, ICFR / SOX internal controls, Virtual CFO, RERA compliance, payroll and bookkeeping — handled end-to-end for businesses across India.",
    keywords:
      "GST compliance, GST advisory, GST return filing, Income Tax compliance, TDS compliance, tax planning India, statutory audit, tax audit 44AB, internal audit, ICFR, SOX compliance, RERA compliance, Virtual CFO, payroll compliance, CA services Coimbatore",
    canonical: "/services",
    jsonLd: SERVICES_JSONLD,
  });
  return (
    <>
      {/* HERO (DARK) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-100 animate-grid-drift" aria-hidden />
        <div className="absolute inset-0 bg-glow-radial" aria-hidden />
        <div className="absolute -left-32 top-20 w-[680px] h-[680px] rounded-full blur-3xl opacity-30 bg-[radial-gradient(circle,rgba(212,255,63,0.25)_0%,transparent_60%)]" aria-hidden />

        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 pt-28 md:pt-36 pb-20 md:pb-24">
          <Reveal>
            <div className="eyebrow eyebrow-voltage mb-6">Services</div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-semibold text-[40px] sm:text-[56px] md:text-[72px] lg:text-[80px] leading-[1.04] tracking-[-0.03em] max-w-5xl">
              Audit. Tax. Compliance.<br />Advisory.
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-3 h-px w-32 bg-voltage" />
          </Reveal>
          <Reveal delay={250}>
            <p className="mt-7 max-w-2xl text-paper/75 text-base md:text-[18px] leading-[1.55]">
              Handled end-to-end. We work across the full lifecycle, from
              routine filings to complex assignments and ongoing financial
              support.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Sticky pillar scroller — travels with scroll */}
      <ServicesStickyNav
        items={PILLARS.map((p) => ({ id: p.id, title: p.title.split(" & ")[0].split(" ").slice(0, 2).join(" ") }))}
      />

      {/* PILLARS — alternating dark/light */}
      {PILLARS.map((p, i) => (
        <PillarSection key={p.id} p={p} idx={i} />
      ))}

      {/* HOW ENGAGEMENTS WORK (LIGHT, surface) */}
      <section className="bg-surface text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10">
            <Reveal className="col-span-12 md:col-span-5">
              <div className="eyebrow mb-4">How engagements work</div>
              <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em]">
                Three ways to work with us.
              </h2>
            </Reveal>
            <Reveal delay={120} className="col-span-12 md:col-span-7 md:pl-8 md:border-l border-[var(--border-light)] flex items-end">
              <p className="text-[15px] md:text-[16px] text-slate-muted leading-relaxed">
                You can engage us for a specific requirement, or work with us
                across multiple areas depending on your business needs.
              </p>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {ENGAGEMENTS.map((e, i) => (
              <Reveal key={e.title} delay={i * 100}>
                <div className="bg-surface-bright rounded-xl p-7 md:p-8 border border-[var(--border-light)] h-full hover:-translate-y-1 transition-transform duration-300">
                  <div className="font-mono text-[10px] text-ink-tertiary tracking-[0.18em]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-4 font-display text-[20px]">{e.title}</h3>
                  <p className="mt-3 text-slate-muted text-[14px] leading-relaxed">{e.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSE CTA (DARK full-bleed) */}
      <section className="relative bg-hero-gradient text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <h2 className="font-display font-semibold text-[32px] md:text-[52px] leading-[1.06] tracking-[-0.025em] max-w-4xl">
              If you&rsquo;re unsure where your requirement fits, we can help you structure it.
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <WhatsAppCTA testId="services-close-whatsapp-cta" />
              <PhoneIconBtn variant="dark" testId="services-close-phone-icon" />
              <MailIconBtn variant="dark" testId="services-close-email-icon" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
